// Brand Wiki — read-only viewer (Phase 1).
//
// Two-pane layout: left tree of pages grouped by category, right rendered
// markdown of the selected page with source citations and metadata.
// Phase 2 will add edit + ingest; Phase 3 sources panel detail; Phase 4 lint.

import { useState, useEffect, useCallback, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Loader2, FileText, ArrowLeft, RefreshCw, ExternalLink, Search, Plus, Pencil, FilePlus, ShieldCheck } from 'lucide-react';
import { coworkApi } from '../api/coworkApi';
import { WikiIngestModal } from './WikiIngestModal';
import { WikiPageEditor } from './WikiPageEditor';
import { WikiLintPanel } from './WikiLintPanel';

interface PageListItem {
  slug: string;
  category: string;
  title: string;
  parentSlug: string | null;
  summary: string;
  sourceCount: number;
  updatedAt: string;
}

interface PageDetail {
  slug: string;
  category: string;
  title: string;
  bodyMd: string;
  renderedMd: string;
  parentSlug: string | null;
  sources: Array<{ id: string; type: string; title: string; url: string | null; ingestedAt: string }>;
  createdAt: string;
  updatedAt: string;
}

const CATEGORY_LABEL: Record<string, string> = {
  identity: 'Brand identity',
  voice: 'Voice & tone',
  visual: 'Visual identity',
  audience: 'Audience',
  persona: 'Personas',
  competitor: 'Competitors',
  pillar: 'Content',
  product: 'Products',
  channel: 'Channels',
  note: 'Notes',
};

const CATEGORY_ORDER = ['identity', 'voice', 'visual', 'audience', 'persona', 'competitor', 'pillar', 'product', 'channel', 'note'];

const SOURCE_TYPE_LABEL: Record<string, string> = {
  url: '🌐 Web',
  pdf: '📄 PDF',
  transcript: '🎙️ Transcript',
  chat_session: '💬 Chat',
  slack: '💬 Slack',
  note: '📝 Note',
  brand_graph: '🌱 Brand graph',
};

interface BrandWikiPageProps {
  onBack: () => void;
  brandId?: string;
  initialSlug?: string;
}

export function BrandWikiPage({ onBack, brandId = 'default', initialSlug }: BrandWikiPageProps) {
  const [pages, setPages] = useState<PageListItem[] | null>(null);
  const [activeSlug, setActiveSlug] = useState<string | null>(initialSlug || null);
  const [activePage, setActivePage] = useState<PageDetail | null>(null);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingPage, setLoadingPage] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [showIngest, setShowIngest] = useState(false);
  const [editorMode, setEditorMode] = useState<{ kind: 'edit'; slug: string } | { kind: 'new' } | null>(null);
  const [showLint, setShowLint] = useState(false);
  const [openLintCount, setOpenLintCount] = useState<number>(0);

  const refresh = useCallback(() => {
    setLoadingList(true);
    coworkApi.wiki.list(brandId)
      .then(d => {
        setPages(d.pages);
        if (!activeSlug && d.pages.length > 0) {
          setActiveSlug(d.pages[0].slug);
        }
      })
      .catch(e => setError(e.message || 'Failed to load wiki'))
      .finally(() => setLoadingList(false));
  }, [brandId, activeSlug]);

  useEffect(() => { refresh(); }, [refresh]);

  // Cheap fetch of open lint count for the badge.
  useEffect(() => {
    coworkApi.wiki.lint(brandId, 'open')
      .then(d => setOpenLintCount(d.findings.length))
      .catch(() => setOpenLintCount(0));
  }, [brandId, showLint]);

  // Listen to in-app cross-link clicks (#/wiki/<slug>) → switch active page
  useEffect(() => {
    const onHashChange = () => {
      const m = window.location.hash.match(/^#\/wiki\/(.+)$/);
      if (m) setActiveSlug(decodeURIComponent(m[1]));
    };
    window.addEventListener('hashchange', onHashChange);
    onHashChange();
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  useEffect(() => {
    if (!activeSlug) { setActivePage(null); return; }
    setLoadingPage(true);
    coworkApi.wiki.get(activeSlug, brandId)
      .then(setActivePage)
      .catch(e => setError(e.message || 'Failed to load page'))
      .finally(() => setLoadingPage(false));
  }, [activeSlug, brandId]);

  const groupedPages = useMemo(() => {
    if (!pages) return null;
    const filtered = search.trim()
      ? pages.filter(p =>
          p.title.toLowerCase().includes(search.toLowerCase()) ||
          p.summary.toLowerCase().includes(search.toLowerCase()))
      : pages;
    return filtered.reduce<Record<string, PageListItem[]>>((acc, p) => {
      (acc[p.category] ||= []).push(p);
      return acc;
    }, {});
  }, [pages, search]);

  return (
    <div className="flex flex-col h-full bg-white">
      <div className="shrink-0 flex items-center gap-3 px-6 py-3 border-b border-[#eaecf0]">
        <button onClick={onBack} className="flex items-center gap-1.5 text-[12px] text-[#98a2b3] hover:text-[#344054]">
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </button>
        <div className="w-px h-4 bg-[#eaecf0]" />
        <h1 className="text-[14px] font-semibold text-[#101828]">Brand Wiki</h1>
        <span className="text-[11px] text-[#94a3b8]">
          {pages?.length ? `${pages.length} pages` : ''}
        </span>
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={() => setShowLint(true)}
            className={`text-[11px] flex items-center gap-1.5 px-2 py-1 rounded hover:bg-[#f8fafc] ${openLintCount > 0 ? 'text-[#dc2626]' : 'text-[#667085] hover:text-[#101828]'}`}
            title={openLintCount > 0 ? `${openLintCount} open finding(s)` : 'Run wiki health check'}
          >
            <ShieldCheck className="w-3 h-3" />
            Lint
            {openLintCount > 0 && (
              <span className="ml-0.5 text-[9px] font-semibold bg-[#dc2626] text-white rounded-full px-1.5 min-w-[14px] text-center">
                {openLintCount}
              </span>
            )}
          </button>
          <button
            onClick={() => setEditorMode({ kind: 'new' })}
            className="text-[11px] text-[#667085] hover:text-[#101828] flex items-center gap-1.5 px-2 py-1 rounded hover:bg-[#f8fafc]"
            title="Create a blank wiki page"
          >
            <FilePlus className="w-3 h-3" /> New page
          </button>
          <button
            onClick={() => setShowIngest(true)}
            className="text-[11px] text-white bg-[#be43ff] hover:bg-[#a93ae0] flex items-center gap-1.5 px-2.5 py-1 rounded font-semibold"
            title="Drop a URL, transcript, or note into the wiki"
          >
            <Plus className="w-3 h-3" /> Add source
          </button>
          <button
            onClick={refresh}
            className="text-[11px] text-[#667085] hover:text-[#101828] flex items-center gap-1.5 px-2 py-1 rounded hover:bg-[#f8fafc]"
            title="Refresh"
          >
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="px-6 py-2 bg-[#fef2f2] text-[12px] text-[#dc2626]">{error}</div>
      )}

      <div className="flex flex-1 min-h-0">
        {/* ── Left: page tree ── */}
        <aside className="w-[280px] shrink-0 border-r border-[#eaecf0] overflow-y-auto">
          <div className="p-3 sticky top-0 bg-white border-b border-[#f1f5f9]">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#94a3b8]" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Filter pages…"
                className="w-full text-[12px] pl-8 pr-3 py-1.5 rounded-md border border-[#e4e7ec] focus:outline-none focus:border-[#be43ff]/50 focus:ring-1 focus:ring-[#be43ff]/20"
              />
            </div>
          </div>

          {loadingList ? (
            <div className="flex justify-center py-10"><Loader2 className="w-4 h-4 animate-spin text-[#94a3b8]" /></div>
          ) : !pages || pages.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="py-2">
              {CATEGORY_ORDER.map(cat => {
                const list = groupedPages?.[cat];
                if (!list?.length) return null;
                return (
                  <div key={cat} className="mb-3">
                    <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-[#94a3b8] font-semibold">
                      {CATEGORY_LABEL[cat] || cat} <span className="text-[#cbd5e1]">· {list.length}</span>
                    </div>
                    {list.map(p => (
                      <button
                        key={p.slug}
                        onClick={() => setActiveSlug(p.slug)}
                        className={`w-full text-left px-3 py-1.5 text-[12px] hover:bg-[#f8f5ff] transition-colors ${
                          activeSlug === p.slug ? 'bg-[#f8f5ff] border-l-2 border-[#be43ff]' : ''
                        }`}
                      >
                        <div className="font-medium text-[#101828] truncate">{p.title}</div>
                        {p.summary && (
                          <div className="text-[10px] text-[#94a3b8] truncate mt-0.5">{p.summary}</div>
                        )}
                      </button>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </aside>

        {/* ── Right: page body ── */}
        <main className="flex-1 overflow-y-auto bg-[#fafafa]">
          {loadingPage && !activePage ? (
            <div className="flex justify-center py-20"><Loader2 className="w-5 h-5 animate-spin text-[#94a3b8]" /></div>
          ) : activePage ? (
            <article className="max-w-3xl mx-auto px-8 py-8">
              <div className="flex items-center justify-between mb-1">
                <div className="text-[10px] uppercase tracking-wider text-[#94a3b8] font-semibold">
                  {CATEGORY_LABEL[activePage.category] || activePage.category}
                </div>
                <button
                  onClick={() => setEditorMode({ kind: 'edit', slug: activePage.slug })}
                  className="text-[11px] text-[#667085] hover:text-[#101828] flex items-center gap-1 px-2 py-0.5 rounded hover:bg-[#f8fafc]"
                  title="Edit this page"
                >
                  <Pencil className="w-3 h-3" /> Edit
                </button>
              </div>
              <div className="prose prose-sm max-w-none prose-headings:font-semibold prose-headings:text-[#101828] prose-p:text-[#344054] prose-a:text-[#be43ff] prose-a:no-underline hover:prose-a:underline prose-strong:text-[#101828] prose-li:text-[#344054]">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {activePage.renderedMd}
                </ReactMarkdown>
              </div>

              {activePage.sources.length > 0 && (
                <div className="mt-10 pt-6 border-t border-[#eaecf0]">
                  <h3 className="text-[11px] uppercase tracking-wider text-[#94a3b8] font-semibold mb-3">Sources</h3>
                  <ul className="space-y-2">
                    {activePage.sources.map(s => (
                      <li key={s.id} className="flex items-start gap-2 text-[12px]">
                        <span className="text-[11px] text-[#667085] shrink-0">{SOURCE_TYPE_LABEL[s.type] || s.type}</span>
                        <div className="flex-1 min-w-0">
                          {s.url ? (
                            <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-[#be43ff] hover:underline inline-flex items-center gap-1">
                              {s.title} <ExternalLink className="w-2.5 h-2.5" />
                            </a>
                          ) : (
                            <span className="text-[#101828]">{s.title}</span>
                          )}
                          <span className="text-[#94a3b8] ml-2">· {new Date(s.ingestedAt).toLocaleDateString()}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mt-8 text-[10px] text-[#cbd5e1]">
                Updated {new Date(activePage.updatedAt).toLocaleString()}
              </div>
            </article>
          ) : (
            <div className="flex items-center justify-center h-full text-[12px] text-[#94a3b8]">
              <div className="text-center">
                <FileText className="w-8 h-8 mx-auto mb-2 opacity-40" />
                Select a page to view it
              </div>
            </div>
          )}
        </main>
      </div>

      {showIngest && (
        <WikiIngestModal
          brandId={brandId}
          onClose={() => setShowIngest(false)}
          onIngested={(r) => {
            // Refresh page list so newly created/updated pages show up;
            // navigate to the first touched slug if any.
            refresh();
            const touched = [...r.created, ...r.updated];
            if (touched.length) setActiveSlug(touched[0]);
          }}
        />
      )}

      {showLint && (
        <WikiLintPanel
          brandId={brandId}
          onClose={() => setShowLint(false)}
          onJumpToPage={(slug) => { setActiveSlug(slug); setShowLint(false); }}
        />
      )}

      {editorMode && (
        <WikiPageEditor
          mode={editorMode}
          brandId={brandId}
          onClose={() => setEditorMode(null)}
          onSaved={(slug) => {
            setEditorMode(null);
            refresh();
            setActiveSlug(slug);
            // Force-reload the active page even if slug is unchanged
            if (activeSlug === slug) {
              coworkApi.wiki.get(slug, brandId).then(setActivePage).catch(() => {});
            }
          }}
          onDeleted={(slug) => {
            setEditorMode(null);
            refresh();
            if (activeSlug === slug) {
              setActiveSlug(null);
              setActivePage(null);
            }
          }}
        />
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="px-4 py-8 text-center">
      <FileText className="w-8 h-8 mx-auto mb-3 text-[#cbd5e1]" />
      <p className="text-[12px] text-[#94a3b8] mb-1">No wiki pages yet.</p>
      <p className="text-[11px] text-[#cbd5e1]">
        Add a brand URL or run brand discovery — pages will populate automatically.
      </p>
    </div>
  );
}
